// TODO-KWF: restore defaults!
// const ENABLED = true;
const ENABLED = false;

type LogLevel = "log" | "warn" | "error" | "info";
type MessageType = "connection" | "console" | "event";

class RemoteEventForwarder {
  private readonly host = "192.168.0.112"; // example - "192.168.1.1"
  private readonly port = 8091;

  private readonly enabled: boolean = ENABLED;
  private readonly shouldRetryOnClose: boolean = true;
  private readonly retryDelay: number = 5000;
  private readonly maxRetries: number = 3;

  // Transient State

  private ws: WebSocket | null = null;
  private retryCount: number = 0;
  private clientId: string | null = null;
  private connectionCallbacks = new Set<() => void>();
  private originalConsole: Console | null = null;
  private isConnected: boolean = false;

  constructor() {
    if (this.enabled) {
      if (!this.host) {
        console.error("RemoteEventForwarder - host is not set");
        return;
      }

      this.connect();
    }
  }

  // Helper method to make objects serializable
  private sanitizeArgs(args: unknown[]): unknown[] {
    return args.map((arg) => {
      try {
        // Test if the argument is serializable
        JSON.stringify(arg);
        return arg;
      } catch {
        // If serialization fails, return a string representation
        return String(arg);
      }
    });
  }

  private interceptConsole() {
    // Save original methods separately before modifying them
    const originalMethods: Record<LogLevel, (...args: unknown[]) => void> = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
    };

    this.originalConsole = console;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const methods: LogLevel[] = ["log", "warn", "error", "info"];

    methods.forEach((method) => {
      console[method] = (...args: unknown[]) => {
        originalMethods[method](...args);

        // Use 'self' instead of 'this' to ensure correct context
        self.send("console", {
          method,
          args: self.sanitizeArgs(args),
          timestamp: Date.now(),
        });
      };
    });
  }

  private connect() {
    try {
      this.ws = new WebSocket(`ws://${this.host}:${this.port}`);

      this.ws.onopen = () => {
        this.isConnected = true;

        this.interceptConsole();

        this.originalConsole?.log("Connected to Lissen Debugger");
        this.retryCount = 0;

        this.connectionCallbacks.forEach((callback) => callback());
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "connection" && message.clientId) {
            this.clientId = message.clientId;
            this.originalConsole?.log(`Received client ID: ${this.clientId}`);
          }
        } catch (error) {
          this.originalConsole?.error("Error parsing message:", error);
        }
      };

      this.ws.onerror = (error) => {
        this.originalConsole?.error("WebSocket error:", error);
      };

      this.ws.onclose = () => {
        this.isConnected = false;

        this.originalConsole?.log("Disconnected from Lissen Debugger");
        this.clientId = null; // Reset clientId when disconnected

        if (this.shouldRetryOnClose && this.retryCount < this.maxRetries) {
          this.retryCount += 1;

          this.originalConsole?.log(
            `Reconnection attempt ${this.retryCount}/${this.maxRetries} in ${this.retryDelay}ms`,
          );
          setTimeout(() => this.connect(), this.retryDelay);
        } else if (this.retryCount >= this.maxRetries) {
          this.originalConsole?.log(
            `Maximum reconnection attempts (${this.maxRetries}) reached.`,
          );
        }
      };
    } catch (error) {
      this.originalConsole?.error(
        "Failed to establish Lissen Debugger connection:",
        error,
      );
    }
  }

  // Public interface

  public addConnectionCallback = (callback: () => void) => {
    this.connectionCallbacks.add(callback);
  };

  public getIsConnected = () => this.isConnected;

  public getIsEnabled = () => this.enabled;

  public removeConnectionCallback = (callback: () => void) => {
    this.connectionCallbacks.delete(callback);
  };

  public send(type: MessageType, data: unknown) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          clientId: this.clientId,
          data,
          type,
        }),
      );
    }
  }
}

export const remoteEventForwarder = new RemoteEventForwarder();
